import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Instagram, Linkedin, Headset } from "lucide-react";

export default function Footer() {
  return (
    <footer className="border-t border-border bg-surface" data-testid="main-footer">
      <div className="mx-auto max-w-screen-2xl px-6 py-16 md:px-12 lg:px-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <p className="font-serif text-3xl font-bold tracking-tight text-[#0C3B2E]">
                Eunoia<span className="align-super text-[0.55em]">™</span>
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/eunoia.stateofmind?igsi=bDR5cnkyeTE4MW05&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Eunoia on Instagram"
                  data-testid="footer-social-instagram"
                  className="text-muted-foreground transition-colors hover:text-accent"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/eunoia4u/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Eunoia on LinkedIn"
                  data-testid="footer-social-linkedin"
                  className="text-muted-foreground transition-colors hover:text-accent"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              <span className="italic">Eunoia</span> (n.) — from the Greek, "beautiful thinking; a well mind."
              Affordable, private, judgment-free therapy for India. Sessions from ₹99.
            </p>
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-border bg-background p-4">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <p className="text-xs leading-relaxed text-muted-foreground" data-testid="footer-helpline">
                Eunoia is not an emergency service. In crisis or thinking of self-harm? Please reach out
                now — <span className="font-semibold text-foreground">KIRAN 1800-599-0019</span> (24×7, free).
                For extreme-case mental health support, please call the national{" "}
                <span className="font-semibold text-foreground">Tele MANAS helpline at 1-800-891-4416</span>.
              </p>
            </div>
            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-border bg-background p-4">
              <Headset className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <p className="text-xs leading-relaxed text-muted-foreground" data-testid="footer-support">
                For any questions or assistance, please feel free to reach out to our support team at{" "}
                <a href="tel:+919740749114" className="font-semibold text-foreground hover:text-accent">
                  +91 97407 49114
                </a>
                .
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link className="transition-colors hover:text-accent" to="/find" data-testid="footer-link-therapists">Find a therapist</Link></li>
              <li><Link className="transition-colors hover:text-accent" to="/how-it-works" data-testid="footer-link-how">How therapy works</Link></li>
              <li><Link className="transition-colors hover:text-accent" to="/pricing" data-testid="footer-link-pricing">Pricing</Link></li>
              <li><Link className="transition-colors hover:text-accent" to="/faqs" data-testid="footer-link-faqs">FAQs</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Learn</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link className="transition-colors hover:text-accent" to="/resources" data-testid="footer-link-resources">Resources</Link></li>
              <li><Link className="transition-colors hover:text-accent" to="/about" data-testid="footer-link-about">About Eunoia</Link></li>
              <li><Link className="transition-colors hover:text-accent" to="/contact" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Our promise</p>
            <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-accent" /> 100% private — no chat records
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-accent" /> Honest pricing — from ₹99
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-accent" /> Qualified, verified psychologists
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 text-xs text-muted-foreground md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} Eunoia. A safe place to talk, to be heard, to find your way back to yourself.</p>
          <div className="flex items-center gap-4">
            <Link className="transition-colors hover:text-accent" to="/privacy" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link className="transition-colors hover:text-accent" to="/terms" data-testid="footer-link-terms">Terms of Service</Link>
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Therapy here is supportive counselling — not a substitute for psychiatric or emergency care.
        </p>
      </div>
    </footer>
  );
}
